import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Launch as LaunchIcon,
  Security as SecurityIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { Risk, Component } from 'shared';
import { useRisks, useDeleteRisk } from '../hooks/useRisks';
import { useSpace } from '../contexts/SpaceContext';
import { useSecurityAssistant } from '../contexts/SecurityAssistantContext';
import { riskApi } from '../services/risk.service';
import { RiskForm } from '../components/RiskForm';
import { RiskPreviewModal } from '../components/RiskPreviewModal';

// =============================================================================
// TYPES
// =============================================================================

// Extended Risk type that includes the component relation from API
interface RiskWithComponent extends Risk {
  component?: Component;
}

type Order = 'asc' | 'desc';
type SortableField =
  | 'title'
  | 'inherentRisk'
  | 'residualRisk'
  | 'status'
  | 'createdAt';

interface HeadCell {
  id: SortableField | 'component' | 'treatment' | 'actions';
  label: string;
  sortable: boolean;
  width?: string;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof Risk>(
  order: Order,
  orderBy: Key
): (a: Risk, b: Risk) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// =============================================================================
// RISK LEVEL CHIP
// =============================================================================

interface RiskLevelChipProps {
  score: number | string;
}

function RiskLevelChip({ score }: RiskLevelChipProps) {
  const { t } = useTranslation();
  // Convert string to number if needed (database may return string)
  const numericScore = typeof score === 'string' ? parseFloat(score) : score;
  const safeScore = isNaN(numericScore) ? 0 : numericScore;

  const level = riskApi.getRiskLevel(safeScore);
  const color = riskApi.getRiskColor(safeScore);

  return (
    <Chip
      label={`${safeScore.toFixed(1)} - ${t(`risk.level.${level}`)}`}
      size="small"
      sx={{
        backgroundColor: `${color}20`,
        color: color,
        fontWeight: 600,
        minWidth: 100,
      }}
    />
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function RiskManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentSpace } = useSpace();
  const { setRiskContext } = useSecurityAssistant();

  // State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<SortableField>('createdAt');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [treatmentFilter, setTreatmentFilter] = useState<string>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editingRisk, setEditingRisk] = useState<RiskWithComponent | null>(
    null
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [riskToDelete, setRiskToDelete] = useState<RiskWithComponent | null>(
    null
  );
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState<RiskWithComponent | null>(
    null
  );

  // Data fetching - API returns Risk with component relation
  const {
    data: risksData,
    isLoading,
    error,
  } = useRisks({
    spaceId: currentSpace?.id,
  });
  // Cast to extended type since API includes component relation
  const risks = risksData as RiskWithComponent[] | undefined;
  const deleteRiskMutation = useDeleteRisk();

  // Table head cells
  const headCells: HeadCell[] = [
    { id: 'title', label: t('risk.fields.title'), sortable: true },
    { id: 'component', label: t('risk.fields.component'), sortable: false },
    {
      id: 'inherentRisk',
      label: t('risk.scores.inherent'),
      sortable: true,
      width: '140px',
    },
    {
      id: 'residualRisk',
      label: t('risk.scores.residual'),
      sortable: true,
      width: '140px',
    },
    {
      id: 'status',
      label: t('risk.fields.status'),
      sortable: true,
      width: '130px',
    },
    {
      id: 'treatment',
      label: t('risk.fields.treatment'),
      sortable: false,
      width: '110px',
    },
    {
      id: 'actions',
      label: t('common.actions'),
      sortable: false,
      width: '100px',
    },
  ];

  // Filtered and sorted data
  const filteredRisks = useMemo(() => {
    if (!risks) return [];

    return risks.filter(risk => {
      // Search filter
      const matchesSearch =
        searchQuery === '' ||
        risk.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        risk.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        risk.threatSource?.toLowerCase().includes(searchQuery.toLowerCase());

      // Status filter
      const matchesStatus =
        statusFilter === 'all' || risk.status === statusFilter;

      // Treatment filter
      const matchesTreatment =
        treatmentFilter === 'all' || risk.riskTreatment === treatmentFilter;

      return matchesSearch && matchesStatus && matchesTreatment;
    });
  }, [risks, searchQuery, statusFilter, treatmentFilter]);

  const sortedRisks = useMemo(() => {
    const sorted = [...filteredRisks].sort(
      getComparator(order, orderBy as keyof Risk)
    );
    return sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredRisks, order, orderBy, page, rowsPerPage]);

  // Handlers
  const handleRequestSort = (property: SortableField) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<globalThis.HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateClick = () => {
    setEditingRisk(null);
    setFormOpen(true);
  };

  const handleEdit = (risk: Risk) => {
    setEditingRisk(risk);
    setFormOpen(true);
  };

  const handleDelete = (risk: Risk) => {
    setRiskToDelete(risk);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (riskToDelete) {
      await deleteRiskMutation.mutateAsync(riskToDelete.id);
      setDeleteDialogOpen(false);
      setRiskToDelete(null);
    }
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingRisk(null);
  };

  const handleRowClick = (risk: RiskWithComponent) => {
    // Set context for security assistant sidebar
    setSelectedRisk(risk);
    setRiskContext({
      riskId: risk.id,
      riskTitle: risk.title,
      componentId: risk.componentId,
      threatSource: risk.threatSource,
      vulnerability: risk.vulnerability,
      inherentRisk: risk.inherentRisk,
      residualRisk: risk.residualRisk,
      spaceId: risk.spaceId,
    });
  };

  const handleRowDoubleClick = (riskId: string) => {
    navigate(`/risks/${riskId}`);
  };

  const handleOpenDetail = (e: React.MouseEvent, riskId: string) => {
    e.stopPropagation();
    navigate(`/risks/${riskId}`);
  };

  // No space selected
  if (!currentSpace) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{t('space.selectToView')}</Alert>
      </Box>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('common.error')}: {(error as Error).message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SecurityIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1">
              {t('risk.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {currentSpace.name} • {filteredRisks.length}{' '}
              {t('risk.title').toLowerCase()}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateClick}
        >
          {t('risk.create')}
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder={t('common.search')}
            size="small"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('risk.fields.status')}</InputLabel>
            <Select
              value={statusFilter}
              label={t('risk.fields.status')}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">
                {t('common.filter')} - {t('risk.fields.status')}
              </MenuItem>
              <MenuItem value="identified">
                {t('risk.status.identified')}
              </MenuItem>
              <MenuItem value="assessed">{t('risk.status.assessed')}</MenuItem>
              <MenuItem value="treatment_planned">
                {t('risk.status.treatment_planned')}
              </MenuItem>
              <MenuItem value="mitigated">
                {t('risk.status.mitigated')}
              </MenuItem>
              <MenuItem value="accepted">{t('risk.status.accepted')}</MenuItem>
              <MenuItem value="closed">{t('risk.status.closed')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('risk.fields.treatment')}</InputLabel>
            <Select
              value={treatmentFilter}
              label={t('risk.fields.treatment')}
              onChange={e => setTreatmentFilter(e.target.value)}
            >
              <MenuItem value="all">
                {t('common.filter')} - {t('risk.fields.treatment')}
              </MenuItem>
              <MenuItem value="mitigate">
                {t('risk.treatment.mitigate')}
              </MenuItem>
              <MenuItem value="accept">{t('risk.treatment.accept')}</MenuItem>
              <MenuItem value="transfer">
                {t('risk.treatment.transfer')}
              </MenuItem>
              <MenuItem value="avoid">{t('risk.treatment.avoid')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              {headCells.map(headCell => (
                <TableCell
                  key={headCell.id}
                  sx={{ fontWeight: 600, width: headCell.width }}
                >
                  {headCell.sortable ? (
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() =>
                        handleRequestSort(headCell.id as SortableField)
                      }
                    >
                      {headCell.label}
                    </TableSortLabel>
                  ) : (
                    headCell.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRisks.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={headCells.length}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography color="text.secondary">
                    {risks?.length === 0
                      ? t('risk.list.empty')
                      : t('common.noResults')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedRisks.map(risk => (
                <TableRow
                  key={risk.id}
                  hover
                  selected={selectedRisk?.id === risk.id}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    cursor: 'pointer',
                  }}
                  onClick={() => handleRowClick(risk)}
                  onDoubleClick={() => handleRowDoubleClick(risk.id)}
                >
                  <TableCell>
                    <Box sx={{ maxWidth: 300 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {risk.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {risk.threatSource}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    {risk.component ? (
                      <Tooltip title={t('component.viewDetails')}>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() =>
                            navigate(`/components/${risk.componentId}`)
                          }
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          {risk.component.name}
                          <LaunchIcon fontSize="small" />
                        </Link>
                      </Tooltip>
                    ) : risk.componentId ? (
                      <Tooltip title={t('component.viewDetails')}>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() =>
                            navigate(`/components/${risk.componentId}`)
                          }
                        >
                          {risk.componentId.substring(0, 8)}...
                        </Link>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <RiskLevelChip score={risk.inherentRisk} />
                  </TableCell>
                  <TableCell>
                    <RiskLevelChip score={risk.residualRisk} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`risk.status.${risk.status}`)}
                      size="small"
                      variant="outlined"
                      color={
                        risk.status === 'closed' || risk.status === 'mitigated'
                          ? 'success'
                          : risk.status === 'identified'
                            ? 'warning'
                            : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`risk.treatment.${risk.riskTreatment}`)}
                      size="small"
                      sx={{
                        backgroundColor:
                          risk.riskTreatment === 'mitigate'
                            ? '#00D9FF20'
                            : risk.riskTreatment === 'accept'
                              ? '#FFB30020'
                              : '#2D3B4520',
                        color:
                          risk.riskTreatment === 'mitigate'
                            ? '#00D9FF'
                            : risk.riskTreatment === 'accept'
                              ? '#FFB300'
                              : '#2D3B45',
                      }}
                    />
                  </TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    <Tooltip title={t('common.openDetail')}>
                      <IconButton
                        size="small"
                        onClick={e => handleOpenDetail(e, risk.id)}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.edit')}>
                      <IconButton size="small" onClick={() => handleEdit(risk)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.delete')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(risk)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredRisks.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('common.rowsPerPage')}
        />
      </TableContainer>

      {/* Risk Form Dialog - RiskForm has its own Dialog */}
      <RiskForm
        open={formOpen}
        onClose={handleFormClose}
        onSubmit={async _data => {
          // The RiskForm handles create/update via the onSubmit prop
          // It will be called with CreateRiskDto or UpdateRiskDto
          handleFormClose();
        }}
        componentId="" // No specific component - space-level risk
        spaceId={currentSpace.id}
        initialData={editingRisk || undefined}
        mode={editingRisk ? 'edit' : 'create'}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('risk.delete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('risk.deleteConfirm')}</Typography>
          {riskToDelete && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <strong>{riskToDelete.title}</strong>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleteRiskMutation.isPending}
          >
            {deleteRiskMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('common.delete')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Risk Preview Modal */}
      <RiskPreviewModal
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedRisk(null);
        }}
        risk={selectedRisk}
        component={selectedRisk?.component}
      />
    </Box>
  );
}
